import React, { useMemo, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Button, Card, Text } from '@musa.tech/design-system';
import { AppliedFilterChips, type FilterChip } from '@/components/shared/filters/AppliedFilterChips';
import { FilterDropdownButton } from '@/components/shared/filters/FilterDropdownButton';
import { OptionsFilterDropdown, type FilterOption } from '@/components/shared/filters/OptionsFilterDropdown';
import { PillRange } from '@/components/shared/filters/PillRange';
import { getEstablishmentIdsForGenerators, useFilterOptions } from '@/hooks/useFilterOptions';
import { useGatewayMaterials } from '@/hooks/useGatewayMaterials';
import { useGatewayPackagings } from '@/hooks/useGatewayPackagings';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useAppSelector } from '@/store/hooks';
import { useDemoMode } from '@/contexts/DemoModeContext';
import { maskEstablishmentName, maskGeneratorName } from '@/utils/demoMode';
import { OnDemandRequestStatus, BucketMovementStatus, BucketMovementType } from '@/integrations/agreements/client';
import { REQUEST_STATUS_DISPLAY } from '@/utils/onDemandRequestRules';
import { BUCKET_STATUS_DISPLAY, BUCKET_TYPE_LABELS } from '@/utils/bucketRequestRules';
import type { OnDemandService } from './ServiceTypeToggle';
import {
  EMPTY_ON_DEMAND_LIST_FILTERS,
  buildFilterChips,
  isGeneratorPending,
  removeFilter,
  sortOptionsWithOthersLast,
  type OnDemandFilterKey,
  type OnDemandFilterLabels,
  type OnDemandListFilters,
} from './onDemandListFilters';

const REQUEST_STATUS_OPTIONS: FilterOption[] = [
  OnDemandRequestStatus.PENDING,
  OnDemandRequestStatus.ACCEPTED,
  OnDemandRequestStatus.REJECTED,
  OnDemandRequestStatus.CANCELED,
].map((status) => ({ value: String(status), label: REQUEST_STATUS_DISPLAY[status].label }));

const MOVEMENT_STATUS_OPTIONS: FilterOption[] = [
  BucketMovementStatus.PENDING_HAULER,
  BucketMovementStatus.PLANNED,
  BucketMovementStatus.DONE,
  BucketMovementStatus.BLOCKED,
  BucketMovementStatus.CANCELLED,
].map((status) => ({ value: String(status), label: BUCKET_STATUS_DISPLAY[status].label }));

const MOVEMENT_TYPE_OPTIONS: FilterOption[] = [
  BucketMovementType.PLACEMENT,
  BucketMovementType.REMOVAL,
  BucketMovementType.SWAP,
].map((type) => ({ value: String(type), label: BUCKET_TYPE_LABELS[type] }));

const toLabelMap = (options: FilterOption[]) => Object.fromEntries(options.map((o) => [o.value, o.label]));

const single = (value: string | null) => (value ? [value] : []);

export interface OnDemandFilterBarProps {
  service: OnDemandService;
  filters: OnDemandListFilters;
  onChange: (filters: OnDemandListFilters) => void;
}

/**
 * Filtros de "Solicitações Sob Demanda" no fluxo novo — o mesmo desenho da tela anterior
 * (principais à vista, o resto em "Ver mais", chips do que está aplicado).
 *
 * Coleta e movimentação têm filtros diferentes porque o serviço filtra coisas diferentes em
 * cada listagem: resíduo, acondicionamento e tipo de operação só existem na movimentação.
 */
export const OnDemandFilterBar: React.FC<OnDemandFilterBarProps> = ({ service, filters, onChange }) => {
  const isBucket = service === 'bucket';
  const { isDemoMode } = useDemoMode();
  const { trackButtonClick } = useAnalytics();
  const [expanded, setExpanded] = useState(false);

  const rawEstablishments = useAppSelector((s) => s.establishmentAccess.establishments);
  const rawGenerators = useAppSelector((s) => s.establishmentAccess.generators);
  const { establishments, generators } = useFilterOptions({
    selectedGeneratorExternalIds: filters.generatorId ? [filters.generatorId] : undefined,
  });
  const materials = useGatewayMaterials({ enabled: isBucket });
  const packagings = useGatewayPackagings({ enabled: isBucket });

  const establishmentOptions = useMemo<FilterOption[]>(
    () => establishments.map((e) => ({
      value: e.establishment_numeric_id,
      label: maskEstablishmentName(e.establishment_name, isDemoMode),
    })),
    [establishments, isDemoMode],
  );

  const generatorOptions = useMemo<FilterOption[]>(
    () => generators.map((g) => ({ value: g.generator_id, label: maskGeneratorName(g.generator_name, isDemoMode) })),
    [generators, isDemoMode],
  );

  const materialOptions = useMemo<FilterOption[]>(
    () => sortOptionsWithOthersLast((materials.data ?? []).map((m) => ({ value: String(m.id), label: m.name }))),
    [materials.data],
  );

  const packagingOptions = useMemo<FilterOption[]>(
    () => sortOptionsWithOthersLast((packagings.data ?? []).map((p) => ({ value: String(p.id), label: p.name }))),
    [packagings.data],
  );

  const statusOptions = isBucket ? MOVEMENT_STATUS_OPTIONS : REQUEST_STATUS_OPTIONS;

  const labels = useMemo<OnDemandFilterLabels>(() => ({
    gerador: toLabelMap(generatorOptions),
    estabelecimento: toLabelMap(establishmentOptions),
    residuo: toLabelMap(materialOptions),
    acondicionamento: toLabelMap(packagingOptions),
    status: toLabelMap(statusOptions),
    operacao: toLabelMap(MOVEMENT_TYPE_OPTIONS),
  }), [generatorOptions, establishmentOptions, materialOptions, packagingOptions, statusOptions]);

  const chips = useMemo<FilterChip<OnDemandFilterKey>[]>(() => buildFilterChips(filters, labels), [filters, labels]);

  // Os filtros aplicam na hora, sem botão de aplicar: cada mudança é a aplicação — mesmo evento
  // da tela anterior (useSolicitacoesFilters.ts), para as duas listagens.
  const applyFilters = (next: OnDemandListFilters, filterKey: string) => {
    trackButtonClick({ buttonLabel: 'sobdemanda_coletas_aplicar_filtro', filter_key: filterKey });
    onChange(next);
  };

  const update = (patch: Partial<OnDemandListFilters>) =>
    applyFilters({ ...filters, ...patch }, Object.keys(patch)[0]);

  // Trocar de gerador só mantém o estabelecimento se ele for do gerador novo.
  const changeGenerator = (generatorId: string | null) => {
    const allowed = getEstablishmentIdsForGenerators(rawEstablishments, rawGenerators, single(generatorId));
    const keepEstablishment = !allowed || (filters.establishmentId != null && allowed.has(filters.establishmentId));
    update({ generatorId, establishmentId: keepEstablishment ? filters.establishmentId : null });
  };

  const showGenerator = generators.length > 1;
  // pelo total do usuário, não pela lista já recortada pelo gerador: gerador com uma unidade só
  // esconderia o filtro que o aviso pede para usar
  const showEstablishment = rawEstablishments.length > 1;

  // Filtro ativo atrás do "Ver mais" nunca fica escondido.
  const secondaryActive = Boolean(filters.generatorId || filters.types.length || filters.dateFrom || filters.dateTo);
  const showSecondary = expanded || secondaryActive;

  const pendingGeneratorName = isGeneratorPending(filters)
    ? (labels.gerador?.[filters.generatorId as string] ?? 'o gerador')
    : null;

  return (
    <div className="flex flex-col gap-3">
      <Card className="w-full bg-primary-50">
        <div className="flex flex-col items-start gap-2 px-4 py-3 md:flex-row">
          <Text variant="label" size="small" className="shrink-0 whitespace-nowrap py-2 text-neutral-700">
            Filtrar por
          </Text>

          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2 md:justify-end" data-testid="on-demand-filters">
            {showEstablishment && (
              <OptionsFilterDropdown
                label="Estabelecimento"
                options={establishmentOptions}
                value={single(filters.establishmentId)}
                multiple={false}
                searchPlaceholder="Buscar estabelecimento..."
                emptyMessage="Nenhum estabelecimento disponível."
                onChange={(values) => update({ establishmentId: values[0] ?? null })}
              />
            )}
            {isBucket && (
              <OptionsFilterDropdown
                label="Resíduo"
                options={materialOptions}
                value={single(filters.materialId)}
                multiple={false}
                isLoading={materials.isLoading}
                searchPlaceholder="Buscar resíduo..."
                onChange={(values) => update({ materialId: values[0] ?? null })}
              />
            )}
            {isBucket && (
              <OptionsFilterDropdown
                label="Acondicionamento"
                options={packagingOptions}
                value={single(filters.packagingId)}
                multiple={false}
                isLoading={packagings.isLoading}
                searchPlaceholder="Buscar acondicionamento..."
                onChange={(values) => update({ packagingId: values[0] ?? null })}
              />
            )}
            <OptionsFilterDropdown
              label="Status"
              options={statusOptions}
              value={filters.statuses.map(String)}
              // a listagem de coletas aceita um status só
              multiple={isBucket}
              onChange={(values) => update({ statuses: values.map(Number) })}
            />

            {showSecondary && (
              <>
                {showGenerator && (
                  <OptionsFilterDropdown
                    label="Gerador"
                    options={generatorOptions}
                    value={single(filters.generatorId)}
                    multiple={false}
                    searchPlaceholder="Buscar gerador..."
                    emptyMessage="Nenhum gerador disponível."
                    onChange={(values) => changeGenerator(values[0] ?? null)}
                  />
                )}
                {isBucket && (
                  <OptionsFilterDropdown
                    label="Tipo de operação"
                    options={MOVEMENT_TYPE_OPTIONS}
                    value={filters.types.map(String)}
                    onChange={(values) => update({ types: values.map(Number) })}
                  />
                )}
                <FilterDropdownButton label="Data" count={[filters.dateFrom, filters.dateTo].filter(Boolean).length}>
                  <PillRange
                    type="date"
                    from={filters.dateFrom}
                    to={filters.dateTo}
                    onFromChange={(dateFrom) => update({ dateFrom })}
                    onToChange={(dateTo) => update({ dateTo })}
                  />
                </FilterDropdownButton>
              </>
            )}

            {!secondaryActive && (
              <Button
                variant="link"
                size="sm"
                className="shrink-0 gap-1 font-inter font-normal text-primary-600 hover:text-primary-700"
                onClick={() => {
                  // mesmo evento do "Ver mais" da tela anterior (GR-712), só ao abrir
                  if (!expanded) trackButtonClick({ buttonLabel: 'sobdemanda_coletas_abrir_filtro' });
                  setExpanded((previous) => !previous);
                }}
              >
                {expanded ? 'Ver menos' : 'Ver mais'}
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${expanded ? 'rotate-180' : ''}`} />
              </Button>
            )}
          </div>
        </div>
      </Card>

      {pendingGeneratorName && (
        <Text variant="body" size="small" className="text-neutral-600" data-testid="on-demand-generator-hint">
          O filtro de gerador só limita a lista de estabelecimentos. Escolha um estabelecimento de{' '}
          {pendingGeneratorName} para filtrar a listagem.
        </Text>
      )}

      {/* Sem type argument no JSX: o lovable-tagger (vite, modo development) injeta um atributo
          logo após o nome da tag e o esbuild não aceita `<Comp<T>`. O tipo vem de `chips`. */}
      <AppliedFilterChips
        chips={chips}
        onRemove={(key, value) => applyFilters(removeFilter(filters, key, value), key)}
        onClearAll={() => applyFilters(EMPTY_ON_DEMAND_LIST_FILTERS, 'limpar_todos')}
      />
    </div>
  );
};
